import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import './CommonStyles.css';
import './Products.css';
import './AllSellStyles.css';
import { exportToCSV, exportToExcel, exportToPDF, printPage } from './exportUtils';
import apiService from '../../services/api';

const ExpenseReport = () => {
  const [entriesPerPage, setEntriesPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  // Filters
  const [locationId, setLocationId] = useState('all');
  const [categoryId, setCategoryId] = useState('all');
  const [paymentStatus, setPaymentStatus] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Pending Filters for modal
  const [pendingLocationId, setPendingLocationId] = useState('all');
  const [pendingCategoryId, setPendingCategoryId] = useState('all');
  const [pendingPaymentStatus, setPendingPaymentStatus] = useState('all');
  const [pendingStartDate, setPendingStartDate] = useState('');
  const [pendingEndDate, setPendingEndDate] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Selected Expense View Modal
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);

  // Summary Metrics
  const [summary, setSummary] = useState({
    totalExpenses: 0,
    paidExpenses: 0,
    dueExpenses: 0,
    count: 0,
  });

  const fetchFilterOptions = async () => {
    try {
      const [catRes, locRes] = await Promise.all([
        apiService.getExpenseCategories(),
        apiService.getBusinessLocations(),
      ]);
      const catList = Array.isArray(catRes) ? catRes : (catRes.data || []);
      setCategories(catList);

      const locList = Array.isArray(locRes) ? locRes : (locRes.data || locRes || []);
      setLocations(locList);
    } catch (err) {
      /* console.error('Error fetching filter options:', err); */
    }
  };

  const fetchExpenseData = async () => {
    setIsLoading(true);
    try {
      const params = {
        page: currentPage,
        per_page: entriesPerPage,
        search: searchTerm,
      };

      if (locationId !== 'all') params.location_id = locationId;
      if (categoryId !== 'all') params.category_id = categoryId;
      if (paymentStatus !== 'all') params.payment_status = paymentStatus;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const response = await apiService.getExpenses(params);
      const dataList = response.data || response || [];
      setExpenses(dataList);
      setTotalRecords(response.meta?.total || response.total || dataList.length);

      // Compute summaries
      let total = 0;
      let paid = 0;
      let due = 0;

      dataList.forEach((exp) => {
        const amt = parseFloat(exp.final_total || 0);
        const status = (exp.payment_status || '').toLowerCase();
        total += amt;

        if (status === 'paid') {
          paid += amt;
        } else if (status === 'due' || status === 'partial') {
          due += amt;
        }
      });

      setSummary({
        totalExpenses: total,
        paidExpenses: paid,
        dueExpenses: due,
        count: dataList.length,
      });
    } catch (err) {
      /* console.error('Failed to fetch expense report data', err); */
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    fetchExpenseData();
  }, [currentPage, entriesPerPage, locationId, categoryId, paymentStatus, startDate, endDate]);

  const handleOpenFilters = () => {
    setPendingLocationId(locationId);
    setPendingCategoryId(categoryId);
    setPendingPaymentStatus(paymentStatus);
    setPendingStartDate(startDate);
    setPendingEndDate(endDate);
    setShowFilters(true);
  };

  const handleApplyFilters = () => {
    setLocationId(pendingLocationId);
    setCategoryId(pendingCategoryId);
    setPaymentStatus(pendingPaymentStatus);
    setStartDate(pendingStartDate);
    setEndDate(pendingEndDate);
    setCurrentPage(1);
    setShowFilters(false);
  };

  const handleResetFilters = () => {
    setPendingLocationId('all');
    setPendingCategoryId('all');
    setPendingPaymentStatus('all');
    setPendingStartDate('');
    setPendingEndDate('');
  };

  const handleExport = (format) => {
    const headers = [
      'ACTION',
      'DATE',
      'REFERENCE NO',
      'CATEGORY',
      'LOCATION',
      'PAYMENT STATUS',
      'TOTAL AMOUNT',
      'EXPENSE FOR',
      'NOTES',
    ];
    const rows = expenses.map((exp) => [
      'View',
      exp.transaction_date ? exp.transaction_date.slice(0, 16).replace('T', ' ') : '-',
      exp.ref_no || '-',
      exp.category_name || exp.expense_category?.name || '-',
      exp.location_name || exp.location?.name || '-',
      exp.payment_status ? exp.payment_status.toUpperCase() : '-',
      `$${parseFloat(exp.final_total || 0).toFixed(2)}`,
      exp.expense_for ? `${exp.expense_for.first_name || ''} ${exp.expense_for.last_name || ''}`.trim() : 'N/A',
      exp.additional_notes || '-',
    ]);

    const title = 'Expense Report';
    const filename = 'expense_report';

    if (format === 'CSV') exportToCSV(headers, rows, filename);
    else if (format === 'Excel') exportToExcel(headers, rows, filename);
    else if (format === 'PDF') exportToPDF(title, headers, rows, filename);
    else if (format === 'Print') printPage();

    setShowExportDropdown(false);
  };

  const handleViewDetails = (expense) => {
    setSelectedExpense(expense);
    setShowViewModal(true);
  };

  const formatCurrency = (amount) => `$${parseFloat(amount || 0).toFixed(2)}`;

  const tableHeaders = ['ACTION', 'DATE', 'REFERENCE NO', 'CATEGORY', 'LOCATION', 'PAYMENT STATUS', 'TOTAL AMOUNT', 'EXPENSE FOR', 'NOTES'];

  return (
    <div style={{ paddingTop: '0.5rem' }}>
      {/* ── Summary Metric Cards ── */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '1rem',
          marginBottom: '1.25rem',
        }}
      >
        <div style={{ background: 'var(--etpos-card, #fff)', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '8px', padding: '1rem 1.25rem', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
          <span style={{ fontSize: '0.8rem', color: 'var(--etpos-text-muted, #64748b)', fontWeight: '600', textTransform: 'uppercase' }}>Total Expenses</span>
          <h3 style={{ fontSize: '1.4rem', fontWeight: '700', color: 'var(--etpos-text, #1e293b)', margin: '0.2rem 0 0' }}>
            {formatCurrency(summary.totalExpenses)}
          </h3>
        </div>

        <div style={{ background: 'var(--etpos-card, #fff)', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '8px', padding: '1rem 1.25rem', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
          <span style={{ fontSize: '0.8rem', color: 'var(--etpos-text-muted, #64748b)', fontWeight: '600', textTransform: 'uppercase' }}>Paid Expenses</span>
          <h3 style={{ fontSize: '1.4rem', fontWeight: '700', color: '#10b981', margin: '0.2rem 0 0' }}>
            {formatCurrency(summary.paidExpenses)}
          </h3>
        </div>

        <div style={{ background: 'var(--etpos-card, #fff)', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '8px', padding: '1rem 1.25rem', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
          <span style={{ fontSize: '0.8rem', color: 'var(--etpos-text-muted, #64748b)', fontWeight: '600', textTransform: 'uppercase' }}>Due / Partial Expenses</span>
          <h3 style={{ fontSize: '1.4rem', fontWeight: '700', color: '#f59e0b', margin: '0.2rem 0 0' }}>
            {formatCurrency(summary.dueExpenses)}
          </h3>
        </div>

        <div style={{ background: 'var(--etpos-card, #fff)', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '8px', padding: '1rem 1.25rem', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
          <span style={{ fontSize: '0.8rem', color: 'var(--etpos-text-muted, #64748b)', fontWeight: '600', textTransform: 'uppercase' }}>Total Transactions</span>
          <h3 style={{ fontSize: '1.4rem', fontWeight: '700', color: '#6366f1', margin: '0.2rem 0 0' }}>
            {summary.count}
          </h3>
        </div>
      </div>

      {/* ── Table Container ── */}
      <div style={{ background: 'var(--etpos-card, #fff)', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>

        {/* Table controls */}
        <div className="table-controls" style={{ borderBottom: '1px solid var(--etpos-border, #e2e8f0)', padding: '1rem 1.25rem' }}>
          <div className="table-controls-left">
            <div className="entries-select">
              <span>Show</span>
              <select
                value={entriesPerPage}
                onChange={(e) => {
                  setEntriesPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span>entries</span>
            </div>
          </div>

          <div className="table-controls-right" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {/* Filters Button placed right before Export Button */}
            <button
              onClick={handleOpenFilters}
              style={{
                padding: '0.4rem 0.8rem',
                border: '1px solid var(--etpos-border, #e2e8f0)',
                borderRadius: '6px',
                background: 'var(--etpos-card, #fff)',
                cursor: 'pointer',
                fontSize: '0.85rem',
                color: 'var(--etpos-text, #334155)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem',
                fontWeight: '500'
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
              </svg>
              Filters
            </button>

            <div className="export-dropdown-container" style={{ position: 'relative' }}>
              <button className="export-btn" onClick={() => setShowExportDropdown(!showExportDropdown)} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.4rem 0.8rem', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '6px', background: 'var(--etpos-card, #fff)', cursor: 'pointer', fontSize: '0.85rem', color: 'var(--etpos-text, #334155)' }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                Export ▾
              </button>
              {showExportDropdown && (
                <div className="export-dropdown-menu" style={{ position: 'absolute', top: '100%', right: 0, marginTop: '0.25rem', background: 'var(--etpos-card, #fff)', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '6px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', zIndex: 10, minWidth: '160px' }}>
                  {[{ icon: 'fa-file-excel-o', label: 'Export CSV', action: () => handleExport('CSV') }, { icon: 'fa-file-excel-o', label: 'Export Excel', action: () => handleExport('Excel') }, { icon: 'fa-print', label: 'Print', action: () => handleExport('Print') }, { icon: 'fa-file-pdf-o', label: 'Export PDF', action: () => handleExport('PDF') }].map((item, i) => (
                    <button key={i} onClick={() => { if (item.action) item.action(); setShowExportDropdown(false); }} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%', padding: '0.5rem 1rem', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', fontSize: '0.8125rem', color: 'var(--etpos-text, #475569)' }} onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--etpos-bg, #f1f5f9)'} onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                      <i className={`fa ${item.icon}`} style={{ width: '14px', textAlign: 'center' }} />{item.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <input
              type="text"
              placeholder="Search ref no..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ padding: '0.4rem 0.75rem', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '6px', fontSize: '0.8125rem', outline: 'none', width: '180px', background: 'var(--etpos-bg, #fff)', color: 'var(--etpos-text, inherit)' }}
            />
          </div>
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          <table className="products-table" style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
            <thead>
              <tr style={{ background: 'var(--etpos-bg, #f8fafc)', borderBottom: '1px solid var(--etpos-border, #e2e8f0)' }}>
                {tableHeaders.map(h => (
                  <th key={h} style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #1e293b)', fontWeight: '600', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {expenses.length === 0 ? (
                <tr>
                  <td colSpan="9" style={{ textAlign: 'center', padding: '2.5rem', color: 'var(--etpos-text-muted, #64748b)', fontSize: '0.875rem' }}>
                    {isLoading ? 'Loading data...' : 'No expense data available in report'}
                  </td>
                </tr>
              ) : (
                expenses.map((exp) => {
                  const status = (exp.payment_status || 'due').toLowerCase();
                  const badgeColor = status === 'paid' ? '#059669' : status === 'partial' ? '#2563eb' : '#d97706';
                  const badgeBg = status === 'paid' ? '#ecfdf5' : status === 'partial' ? '#eff6ff' : '#fffbe6';

                  return (
                    <tr key={exp.id} style={{ borderBottom: '1px solid var(--etpos-border, #f1f5f9)' }}>
                      <td style={{ padding: '0.75rem 1rem' }}>
                        <button
                          onClick={() => handleViewDetails(exp)}
                          style={{
                            padding: '0.25rem 0.65rem',
                            borderRadius: '4px',
                            background: '#eff6ff',
                            border: '1px solid #bfdbfe',
                            color: '#2563eb',
                            fontSize: '0.75rem',
                            cursor: 'pointer',
                            fontWeight: '600',
                          }}
                        >
                          View
                        </button>
                      </td>
                      <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #475569)', whiteSpace: 'nowrap' }}>{exp.transaction_date ? exp.transaction_date.slice(0, 16).replace('T', ' ') : '-'}</td>
                      <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', fontWeight: '600', color: '#2563eb' }}>{exp.ref_no || '-'}</td>
                      <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #475569)' }}>{exp.category_name || exp.expense_category?.name || '-'}</td>
                      <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #475569)' }}>{exp.location_name || exp.location?.name || '-'}</td>
                      <td style={{ padding: '0.75rem 1rem' }}>
                        <span style={{ display: 'inline-block', padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600', color: badgeColor, background: badgeBg }}>
                          {status.toUpperCase()}
                        </span>
                      </td>
                      <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', fontWeight: '700', color: 'var(--etpos-text, #1e293b)' }}>{formatCurrency(exp.final_total)}</td>
                      <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #475569)' }}>
                        {exp.expense_for
                          ? `${exp.expense_for.first_name || ''} ${exp.expense_for.last_name || ''}`.trim()
                          : exp.created_by_name || 'N/A'}
                      </td>
                      <td
                        style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text-muted, #64748b)', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={exp.additional_notes}
                      >
                        {exp.additional_notes || '-'}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="pagination" style={{ padding: '0.85rem 1.25rem', borderTop: '1px solid #e2e8f0' }}>
          <div className="pagination-info" style={{ fontSize: '0.8125rem', color: 'var(--etpos-text, #475569)' }}>
            Showing {expenses.length > 0 ? (currentPage - 1) * entriesPerPage + 1 : 0} to{' '}
            {Math.min(currentPage * entriesPerPage, totalRecords)} of {totalRecords} entries
          </div>
          <div className="pagination-controls" style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              className="pagination-btn"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </button>
            <span style={{ display: 'flex', alignItems: 'center', padding: '0 0.5rem', fontSize: '0.875rem', color: 'var(--etpos-text, #475569)' }}>
              Page {currentPage}
            </span>
            <button
              className="pagination-btn"
              disabled={currentPage * entriesPerPage >= totalRecords}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* ── Filters Popup Modal ── */}
      {showFilters && (
        <div className="modal-overlay" onClick={() => setShowFilters(false)}>
          <div className="modal-container filter-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', width: '95%' }}>
            <div className="modal-header">
              <h2 style={{ fontSize: '1.1rem', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
                </svg>
                Filter Expense Report
              </h2>
              <button className="modal-close-btn" onClick={() => setShowFilters(false)}>✕</button>
            </div>
            <div className="modal-content" style={{ padding: '1.5rem', maxWidth: 'none' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: '600', color: 'var(--etpos-text, #475569)', marginBottom: '0.35rem' }}>Start Date:</label>
                  <input type="date" value={pendingStartDate} onChange={e => setPendingStartDate(e.target.value)} style={{ width: '100%', padding: '0.45rem 0.75rem', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '6px', fontSize: '0.875rem', outline: 'none' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: '600', color: 'var(--etpos-text, #475569)', marginBottom: '0.35rem' }}>End Date:</label>
                  <input type="date" value={pendingEndDate} onChange={e => setPendingEndDate(e.target.value)} style={{ width: '100%', padding: '0.45rem 0.75rem', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '6px', fontSize: '0.875rem', outline: 'none' }} />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: '600', color: 'var(--etpos-text, #475569)', marginBottom: '0.35rem' }}>Business Location:</label>
                  <select value={pendingLocationId} onChange={e => setPendingLocationId(e.target.value)} style={{ width: '100%', padding: '0.45rem 0.75rem', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '6px', fontSize: '0.875rem', outline: 'none' }}>
                    <option value="all">All Locations</option>
                    {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: '600', color: 'var(--etpos-text, #475569)', marginBottom: '0.35rem' }}>Expense Category:</label>
                  <select value={pendingCategoryId} onChange={e => setPendingCategoryId(e.target.value)} style={{ width: '100%', padding: '0.45rem 0.75rem', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '6px', fontSize: '0.875rem', outline: 'none' }}>
                    <option value="all">All Categories</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: '600', color: 'var(--etpos-text, #475569)', marginBottom: '0.35rem' }}>Payment Status:</label>
                <select value={pendingPaymentStatus} onChange={e => setPendingPaymentStatus(e.target.value)} style={{ width: '100%', padding: '0.45rem 0.75rem', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '6px', fontSize: '0.875rem', outline: 'none' }}>
                  <option value="all">All Statuses</option>
                  <option value="paid">Paid</option>
                  <option value="due">Due</option>
                  <option value="partial">Partial</option>
                </select>
              </div>
            </div>
            <div style={{ padding: '1rem 1.5rem', background: 'var(--etpos-card, #fff)', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.75rem' }}>
              <button
                type="button"
                onClick={handleResetFilters}
                style={{
                  height: '38px',
                  padding: '0 1.25rem',
                  border: '1px solid var(--etpos-border, #cbd5e1)',
                  borderRadius: '6px',
                  background: 'var(--etpos-bg, #f8fafc)',
                  color: 'var(--etpos-text, #475569)',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.15s ease'
                }}
              >
                Reset
              </button>
              <button
                type="button"
                onClick={handleApplyFilters}
                style={{
                  height: '38px',
                  padding: '0 1.25rem',
                  border: 'none',
                  borderRadius: '6px',
                  background: '#3b82f6',
                  color: '#ffffff',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 1px 2px rgba(59,130,246,0.2)',
                  transition: 'all 0.15s ease'
                }}
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── View Modal Portal ── */}
      {showViewModal &&
        selectedExpense &&
        ReactDOM.createPortal(
          <div
            className="modal-overlay"
            onClick={() => setShowViewModal(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999999,
            }}
          >
            <div
              className="modal-container"
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'var(--etpos-card, white)',
                borderRadius: '12px',
                maxWidth: '600px',
                width: '90%',
                padding: '1.5rem',
                boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderBottom: '1px solid var(--etpos-border, #e2e8f0)',
                  paddingBottom: '0.75rem',
                  marginBottom: '1rem',
                }}
              >
                <h3 style={{ margin: 0, fontSize: '1.15rem', color: 'var(--etpos-text, #1e293b)' }}>
                  Expense Details — {selectedExpense.ref_no}
                </h3>
                <button
                  onClick={() => setShowViewModal(false)}
                  style={{ background: 'none', border: 'none', fontSize: '1.3rem', cursor: 'pointer', color: 'var(--etpos-text-muted, #64748b)' }}
                >
                  ✕
                </button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ fontSize: '0.75rem', color: 'var(--etpos-text-muted, #64748b)', textTransform: 'uppercase', fontWeight: '700' }}>
                    Reference No:
                  </label>
                  <p style={{ margin: '0.2rem 0', fontWeight: '700', color: 'var(--etpos-text, #1e293b)' }}>{selectedExpense.ref_no || '-'}</p>
                </div>
                <div>
                  <label style={{ fontSize: '0.75rem', color: 'var(--etpos-text-muted, #64748b)', textTransform: 'uppercase', fontWeight: '700' }}>
                    Date:
                  </label>
                  <p style={{ margin: '0.2rem 0', color: 'var(--etpos-text, #1e293b)' }}>
                    {selectedExpense.transaction_date ? selectedExpense.transaction_date.slice(0, 16).replace('T', ' ') : '-'}
                  </p>
                </div>
                <div>
                  <label style={{ fontSize: '0.75rem', color: 'var(--etpos-text-muted, #64748b)', textTransform: 'uppercase', fontWeight: '700' }}>
                    Category:
                  </label>
                  <p style={{ margin: '0.2rem 0', color: 'var(--etpos-text, #1e293b)' }}>
                    {selectedExpense.category_name || selectedExpense.expense_category?.name || '-'}
                  </p>
                </div>
                <div>
                  <label style={{ fontSize: '0.75rem', color: 'var(--etpos-text-muted, #64748b)', textTransform: 'uppercase', fontWeight: '700' }}>
                    Location:
                  </label>
                  <p style={{ margin: '0.2rem 0', color: 'var(--etpos-text, #1e293b)' }}>
                    {selectedExpense.location_name || selectedExpense.location?.name || '-'}
                  </p>
                </div>
                <div>
                  <label style={{ fontSize: '0.75rem', color: 'var(--etpos-text-muted, #64748b)', textTransform: 'uppercase', fontWeight: '700' }}>
                    Payment Status:
                  </label>
                  <p style={{ margin: '0.2rem 0' }}>
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '0.2rem 0.5rem',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        color: selectedExpense.payment_status === 'paid' ? '#059669' : selectedExpense.payment_status === 'partial' ? '#2563eb' : '#d97706',
                        background: selectedExpense.payment_status === 'paid' ? '#ecfdf5' : selectedExpense.payment_status === 'partial' ? '#eff6ff' : '#fffbe6'
                      }}
                    >
                      {(selectedExpense.payment_status || 'due').toUpperCase()}
                    </span>
                  </p>
                </div>
                <div>
                  <label style={{ fontSize: '0.75rem', color: 'var(--etpos-text-muted, #64748b)', textTransform: 'uppercase', fontWeight: '700' }}>
                    Total Amount:
                  </label>
                  <p style={{ margin: '0.2rem 0', fontWeight: '700', fontSize: '1.1rem', color: '#2563eb' }}>
                    {formatCurrency(selectedExpense.final_total)}
                  </p>
                </div>
              </div>

              {selectedExpense.additional_notes && (
                <div style={{ marginBottom: '1rem', background: 'var(--etpos-bg, #f8fafc)', padding: '0.75rem', borderRadius: '8px' }}>
                  <label style={{ fontSize: '0.75rem', color: 'var(--etpos-text-muted, #64748b)', textTransform: 'uppercase', fontWeight: '700' }}>
                    Additional Notes:
                  </label>
                  <p style={{ margin: '0.25rem 0 0', fontSize: '0.875rem', color: 'var(--etpos-text, #334155)' }}>
                    {selectedExpense.additional_notes}
                  </p>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '1rem', borderTop: '1px solid #e2e8f0' }}>
                <button className="pagination-btn" onClick={() => setShowViewModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default ExpenseReport;
